import * as React from 'react'
import { twMerge } from 'tailwind-merge'

import { Dialog as DialogPrimitive } from 'radix-ui'
import { IconButton } from '@/components/icon-button/IconButton'
import { XClose } from '@/assets/icons'

export type ModalHeaderProps = {
  /** Title of the modal */
  title?: string
  /** Subtitle of the modal */
  subtitle?: string
  /** Alignment of the title. Default: 'left' */
  titleAlign?: 'left' | 'center'
  /** Icon to display in the title. Default: undefined */
  titleIcon?: React.ReactNode
  /** Show the close button in the header. Default: false */
  showCloseButton?: boolean
}

/**
 * Title, optional subtitle, and optional close control — shared by standard and tabbed modals.
 */
export function ModalHeader({ title, subtitle, titleAlign = 'left', titleIcon, showCloseButton = false }: ModalHeaderProps) {
  if (!title && !subtitle && !showCloseButton) return null

  return (
    <div className={showCloseButton ? 'relative' : undefined}>
      {title && (
        <DialogPrimitive.Title
          className={twMerge(
            'flex items-center gap-x-2 font-rethink-sans text-xl font-semibold leading-7 tracking-normal text-brand-neutral-950',
            titleAlign === 'left' && 'justify-start',
            titleAlign === 'center' && 'justify-center',
            showCloseButton && 'pr-6'
          )}
        >
          {titleIcon && <span className="shrink-0 self-start">{titleIcon}</span>}
          {title}
        </DialogPrimitive.Title>
      )}
      {subtitle && (
        <DialogPrimitive.Description
          className={twMerge(
            'font-sans text-base font-normal leading-6 tracking-[-0.32px] text-brand-neutral-950 mt-1.5',
            titleAlign === 'left' && 'text-left',
            titleAlign === 'center' && 'text-center',
            showCloseButton && 'pr-6'
          )}
        >
          {subtitle}
        </DialogPrimitive.Description>
      )}
      {showCloseButton && (
        // -right-1 -top-1 offsets the 36px wrapper div by 4px so the smaller centered hover circle lands flush at the corner.
        <DialogPrimitive.Close asChild>
          <IconButton aria-label="Close" color="neutral" icon={<XClose />} size="md" variant="plain" wrapperClassName="absolute -right-1 -top-1" />
        </DialogPrimitive.Close>
      )}
    </div>
  )
}
